import { useMemo } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useDxReportDrafts } from '../../../../hooks/useDxReportDrafts';
import type { TimelineEvent } from '../../../patient-files/actions/types';
import { DxDraftPreview } from './DxDraftPreview';

interface DxDraftPanelProps {
    patientId: string;
    /** Render without outer card chrome — for embedding inside a sidebar tab. */
    embedded?: boolean;
}

const PENDING_STATUSES = new Set<TimelineEvent['status']>(['draft', 'pending_review']);

export function DxDraftPanel({ patientId, embedded = false }: DxDraftPanelProps) {
    const { drafts, isLoading, error } = useDxReportDrafts(patientId);
    const dxHref = `/patients/${encodeURIComponent(patientId)}?tab=actions&action=dx_report`;

    // Most recently updated in-progress / pending-review Dx draft, if any.
    const pendingDraft = useMemo<TimelineEvent | null>(() => {
        const pending = drafts
            .filter((d) => PENDING_STATUSES.has(d.status))
            .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        return pending[0] ?? null;
    }, [drafts]);

    const inReview = pendingDraft?.status === 'pending_review';

    const statusPill = pendingDraft ? (
        <span
            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${
                inReview ? 'bg-blue-50 text-blue-700' : 'bg-yellow-50 text-yellow-700'
            }`}
        >
            <span
                className={`h-1.5 w-1.5 rounded-full ${inReview ? 'bg-blue-500' : 'bg-yellow-400'}`}
            />
            {inReview ? 'Pending review' : 'In progress'}
        </span>
    ) : null;

    const openDxLink = (
        <a
            href={dxHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 flex-shrink-0 group"
        >
            <span className="text-sm text-gray-800 group-hover:text-gray-600 transition-colors">
                Open Dx
            </span>
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-colors" />
        </a>
    );

    const body = (
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 scrollbar-hide">
            {isLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="h-3 w-3 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
                    Checking for a draft…
                </div>
            ) : error ? (
                <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-red-400" />
                    <span className="text-sm text-red-500">Couldn't check for a Dx draft.</span>
                </div>
            ) : pendingDraft ? (
                <DxDraftPreview patientId={patientId} draft={pendingDraft} />
            ) : (
                <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-gray-300" />
                    <span className="text-sm text-gray-500">
                        No pending Dx draft for this case.
                    </span>
                </div>
            )}
        </div>
    );

    if (embedded) {
        return (
            <div className="h-full flex flex-col min-h-0 font-inter">
                <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2 flex-shrink-0">
                    <div className="flex items-center gap-2 min-w-0">{statusPill}</div>
                    {openDxLink}
                </div>
                {body}
            </div>
        );
    }

    return (
        <div className="h-full bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden font-inter">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-bold text-sm text-gray-800">Dx report</span>
                    {statusPill}
                </div>
                {openDxLink}
            </div>
            {body}
        </div>
    );
}
